import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, downloadFile } from '@/lib/api/client'
import { asList } from '@/lib/api/list'

/**
 * Belge takibi: ek belge isteme, hatırlatma, inceleme ve "Belgelerim".
 * İK sorumluluğu documents:employee:followup yetkisiyle belirlenir (firma sahipleri dahil).
 */
export const DOC_FOLLOWUP_PERM = 'documents:employee:followup'
export const DOC_REVIEW_PERM = 'documents:employee:review'

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))
const day = (v: unknown) => s(v).slice(0, 10)

const employeeKeys = (id: string) => [
  ['hr', 'employees', id, 'cases'],
  ['hr', 'employees', id, 'missing'],
  ['documents', 'reminders', id],
]

function useInvalidate() {
  const qc = useQueryClient()
  return (employeeIds: string[]) =>
    Promise.all([
      ...employeeIds.flatMap((id) => employeeKeys(id).map((queryKey) => qc.invalidateQueries({ queryKey }))),
      qc.invalidateQueries({ queryKey: ['documents', 'review-queue'] }),
      qc.invalidateQueries({ queryKey: ['documents', 'mine'] }),
    ])
}

export function useRequestDocuments() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (input: { templateId: string; employeeIds: string[]; dueAt: string; note: string }) =>
      api.post<{ created?: number; skipped?: number }>('/api/v1/documents/requests', {
        template_id: input.templateId,
        employee_ids: input.employeeIds,
        due_at: input.dueAt,
        note: input.note,
      }),
    onSuccess: (_d, v) => invalidate(v.employeeIds),
  })
}

export function useRemindEmployee() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ employeeId, note }: { employeeId: string; note: string }) => api.post<{ sent?: number }>(`/api/v1/documents/employees/${employeeId}/remind`, { note }),
    onSuccess: (_d, v) => invalidate([v.employeeId]),
  })
}

export type ReminderEntry = { kind: 'auto' | 'manual'; recipient: string; sentBy: string; note: string; openCount: number; at: string }

export const useReminderHistory = (employeeId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['documents', 'reminders', employeeId],
    enabled: enabled && !!employeeId,
    queryFn: async () =>
      asList(await api.get<Row[]>(`/api/v1/documents/employees/${employeeId}/reminders`)).map(
        (r): ReminderEntry => ({
          kind: r.kind === 'manual' ? 'manual' : 'auto',
          recipient: s(r.recipient_name),
          sentBy: s(r.sent_by_name),
          note: s(r.note),
          openCount: Number(r.open_count) || 0,
          at: s(r.created_at),
        }),
      ),
  })

export type ReviewItem = { caseId: string; employeeId: string; employeeName: string; title: string; note: string; dueAt: string; submittedAt: string; revision: number }

export const useReviewQueue = (enabled: boolean) =>
  useQuery({
    queryKey: ['documents', 'review-queue'],
    enabled,
    queryFn: async () =>
      asList(await api.get<Row[]>('/api/v1/documents/review-queue')).map(
        (r): ReviewItem => ({
          caseId: s(r.case_id),
          employeeId: s(r.employee_id),
          employeeName: s(r.employee_name),
          title: s(r.title),
          note: s(r.request_note),
          dueAt: day(r.due_at),
          submittedAt: s(r.submitted_at),
          revision: Number(r.revision) || 1,
        }),
      ),
  })

export type ReviewDecision = 'approved' | 'correction_requested' | 'rejected'

export function useReviewCase() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ caseId, decision, reason }: { caseId: string; employeeId: string; decision: ReviewDecision; reason?: string }) =>
      api.post(`/api/v1/documents/cases/${caseId}/reviews`, { decision, reason: reason || undefined }),
    onSuccess: (_d, v) => invalidate([v.employeeId]),
  })
}

export const downloadCaseFile = (caseId: string, name: string) => downloadFile(`/api/v1/documents/cases/${caseId}/file`, name)

// --- Belgelerim ---

export type MyCase = { id: string; title: string; status: string; dueAt: string; note: string; isRequired: boolean; createdAt: string }
export type MyDocuments = { cases: MyCase[]; missing: { requirementId: string; name: string }[] }

export const useMyDocuments = () =>
  useQuery({
    queryKey: ['documents', 'mine'],
    queryFn: async (): Promise<MyDocuments> => {
      const row = await api.get<Row>('/api/v1/me/documents')
      const notes = (row.request_notes ?? {}) as Record<string, string>
      const cases = asList(((row.cases as Row | undefined)?.items ?? row.cases ?? []) as Row[])
      return {
        cases: cases.map((c) => ({
          id: s(c.id),
          title: s(c.title),
          status: s(c.status),
          dueAt: day(c.due_at),
          note: notes[s(c.id)] ?? '',
          isRequired: c.is_required !== false,
          createdAt: s(c.created_at),
        })),
        missing: asList((row.missing ?? []) as Row[]).map((m) => ({ requirementId: s(m.requirement_id), name: s(m.name) })),
      }
    },
  })

export function useSubmitMyDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ caseId, file, note }: { caseId: string; file: File; note?: string }) => {
      const form = new FormData()
      form.append('file', file)
      if (note) form.append('note', note)
      return api.post(`/api/v1/me/documents/${caseId}/submit`, form)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', 'mine'] }),
  })
}

export const downloadMyFile = (caseId: string, name: string) => downloadFile(`/api/v1/me/documents/${caseId}/file`, name)
